import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { SupportKind, SupportSeverity } from '@/shared/types/domain'

/** Los filtros viven en la URL, como en Vencimientos: un link comparte la vista. */
export const PARAM = {
  view: 'vista',
  state: 'estado',
  severity: 'severidad',
  kind: 'tipo',
  app: 'app',
  reporter: 'usuario',
} as const

export type SupportState = 'open' | 'done' | 'all'
/** `tickets`: la lista de reportes. `users`: el panel por usuario. `apps`: las apps conectadas. */
export type SupportView = 'tickets' | 'users' | 'apps'

export interface SupportFilters {
  view: SupportView
  state: SupportState
  severity: SupportSeverity | null
  kind: SupportKind | null
  app: string | null
  /** El email de quien reportó: lo pone el panel por usuario. */
  reporter: string | null
}

const SEVERITIES: readonly SupportSeverity[] = ['urgent', 'high', 'medium', 'low']
const KINDS: readonly SupportKind[] = ['bug', 'improvement', 'feature']
const STATES: readonly SupportState[] = ['open', 'done', 'all']
const VIEWS: readonly SupportView[] = ['tickets', 'users', 'apps']

function pick<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : null
}

export function parseSupportFilters(params: URLSearchParams): SupportFilters {
  return {
    view: pick(params.get(PARAM.view), VIEWS) ?? 'tickets',
    state: pick(params.get(PARAM.state), STATES) ?? 'open',
    severity: pick(params.get(PARAM.severity), SEVERITIES),
    kind: pick(params.get(PARAM.kind), KINDS),
    app: params.get(PARAM.app) || null,
    reporter: params.get(PARAM.reporter) || null,
  }
}

/** Lo que viaja al backend: `GET /support/tickets?status=&severity=&kind=&tool_slug=&reporter=`. */
export function toSupportQuery(filters: SupportFilters): Record<string, string> {
  const query: Record<string, string> = { status: filters.state }
  if (filters.severity) query.severity = filters.severity
  if (filters.kind) query.kind = filters.kind
  if (filters.app) query.tool_slug = filters.app
  if (filters.reporter) query.reporter = filters.reporter
  return query
}

export function useSupportFilters() {
  const [params, setParams] = useSearchParams()
  const filters = useMemo(() => parseSupportFilters(params), [params])

  const setFilter = useCallback(
    <K extends keyof SupportFilters>(key: K, value: SupportFilters[K] | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          // `open` y `tickets` son los defaults: no ensucian la URL.
          const porDefecto =
            (key === 'state' && value === 'open') || (key === 'view' && value === 'tickets')
          if (value === null || porDefecto) next.delete(PARAM[key])
          else next.set(PARAM[key], String(value))
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  /** Del panel por usuario a sus reportes: todos, no sólo los abiertos. */
  const showReporter = useCallback(
    (email: string) =>
      setParams(
        () => {
          const next = new URLSearchParams()
          next.set(PARAM.reporter, email)
          next.set(PARAM.state, 'all')
          return next
        },
        { replace: true },
      ),
    [setParams],
  )

  // Limpiar los filtros no cambia de vista: se queda donde estaba.
  const clearFilters = useCallback(
    () =>
      setParams(
        (current) => {
          const next = new URLSearchParams()
          const vista = current.get(PARAM.view)
          if (vista) next.set(PARAM.view, vista)
          return next
        },
        { replace: true },
      ),
    [setParams],
  )

  const isFiltered =
    filters.state !== 'open' ||
    filters.severity !== null ||
    filters.kind !== null ||
    !!filters.app ||
    !!filters.reporter

  return { filters, setFilter, showReporter, clearFilters, isFiltered }
}
